// src/services/crafExecutionService.js
// Mirrors executionService.js but drives the CRAF (Contract Review and Approval
// Form) execution flow on its own SharePoint lists (CRAFExecutions / CRAFApprovers)
// so CRAF approvals are tracked completely separately from the main contract
// execution. Kept as standalone functions (not a shared/parameterized service)
// so the working main-contract flow is never touched by CRAF changes.
import { pdfService } from "./pdfService";

// CRAF PDF file name is deliberately distinct from the main contract's
// "Executed-<title>" name so the two never collide in the same folder.
const getCrafPdfName = (contractRef) => {
  const clean = String(contractRef || "CRAF").replace(/[/\\]/g, "-").trim();
  return `CRAF-Executed-${clean}`;
};

// CRAFExecutions, CRAFApprovers, and FileID's "IsCraf" column are created
// manually in SharePoint ahead of time (same as Executions / ExecutionApprovers
// were) — schema changes need more permission than this app's Graph API token
// has, so there is no auto-provisioning here.

export const crafExecutionService = {
  getCrafPdfName,

  // Create a new CRAF execution workflow
  async initiateCrafExecution(graphService, {
    contractId,
    contractRef,
    contractTitle,
    approvers, // Array of { displayName, mail }
    initiatedBy,
    driveId,
    crafDraftPath
  }) {
    try {
      if (!driveId || !crafDraftPath) {
        throw new Error('Missing driveId or crafDraftPath - cannot generate PDF');
      }

      console.log('[crafExecutionService] 🔄 Step 1: Generating PDF from CRAF draft...');
      let pdfBytes = null;
      const pdfFileName = getCrafPdfName(contractRef);
      try {
        pdfBytes = await pdfService.convertDraftToPdf(
          graphService,
          driveId,
          crafDraftPath,
          contractTitle
        );
        console.log('[crafExecutionService] ✅ PDF Generated:', pdfFileName, '- Bytes:', pdfBytes?.byteLength);
      } catch (pdfError) {
        console.error('[crafExecutionService] ❌ PDF Generation Failed:', pdfError.message);
        throw new Error(`Cannot generate CRAF PDF: ${pdfError.message}`);
      }

      if (!pdfBytes) throw new Error('CRAF PDF generation produced no data');

      // Save initial PDF to SharePoint
      console.log('[crafExecutionService] 🔄 Step 2: Saving CRAF PDF to SharePoint...');
      const folderPath = crafDraftPath.substring(0, crafDraftPath.lastIndexOf('/'));
      let savedPdf;
      try {
        savedPdf = await pdfService.savePdfToSharePoint(
          graphService,
          driveId,
          folderPath,
          pdfBytes,
          pdfFileName
        );
        console.log('[crafExecutionService] ✅ CRAF PDF Saved to SharePoint');
      } catch (saveError) {
        console.error('[crafExecutionService] ❌ Failed to save CRAF PDF:', saveError.message);
        throw new Error(`Cannot save CRAF PDF to SharePoint: ${saveError.message}`);
      }

      // Tag the PDF in FileID metadata so it shows up in the Executed Contract tab
      if (savedPdf?.id) {
        try {
          await graphService.createListItem("FileID", {
            FileID: savedPdf.id,
            FilePath: `${pdfFileName}.pdf`,
            DocumentType: "executed_contract",
            IsCraf: "Yes",
            CreatedDate: new Date().toISOString(),
          });
        } catch (metaErr) {
          console.warn('[crafExecutionService] Could not tag CRAF PDF metadata:', metaErr.message);
        }
      }

      // Create CRAFExecutions record
      console.log('[crafExecutionService] 🔄 Step 3: Creating CRAF execution record...');
      const execution = await graphService.createListItem("CRAFExecutions", {
        Title: `CRAF Execution - ${contractRef}`,
        ContractID: contractId,
        ContractRefNo: contractRef,
        ContractTitle: contractTitle,
        Status: "In Progress",
        InitiatedBy: initiatedBy,
        InitiatedAt: new Date().toISOString(),
        TotalApprovers: approvers.length,
        ApproversCompleted: 0
      });

      const executionId = execution.id;
      console.log('[crafExecutionService] ✅ CRAF Execution Created:', executionId);

      // Create CRAFApprovers records for each approver
      const approverRecords = [];
      for (let i = 0; i < approvers.length; i++) {
        const approver = approvers[i];
        const record = await graphService.createListItem("CRAFApprovers", {
          Title: `${contractRef} - CRAF Approver ${i + 1}`,
          ExecutionID: executionId,
          ContractID: contractId,
          ContractRefNo: contractRef,
          ApproverName: approver.displayName,
          ApproverEmail: approver.mail,
          ApprovalOrder: i + 1,
          Status: i === 0 ? "Pending" : "Waiting",
          CreatedAt: new Date().toISOString()
        });
        approverRecords.push({ ...record, index: i });
      }

      console.log('[crafExecutionService] ✅ Created', approverRecords.length, 'CRAF approver records');

      return {
        executionId,
        approverRecords,
        totalApprovers: approvers.length,
        pdfGenerated: !!pdfBytes
      };
    } catch (error) {
      console.error('[crafExecutionService] Error initiating CRAF execution:', error);
      throw error;
    }
  },

  async getCrafExecution(graphService, executionId) {
    try {
      const execution = await graphService.getListItem("CRAFExecutions", executionId);
      return { ...execution.fields, id: execution.id };
    } catch (error) {
      console.error('[crafExecutionService] Error getting CRAF execution:', error);
      return null;
    }
  },

  async getCrafExecutionApprovers(graphService, executionId) {
    try {
      const data = await graphService.getListItems(
        "CRAFApprovers",
        `fields/ExecutionID eq '${executionId}'`
      );
      return (data?.value || [])
        .map(item => ({ ...item.fields, id: item.id }))
        .sort((a, b) => (a.ApprovalOrder || 0) - (b.ApprovalOrder || 0));
    } catch (error) {
      console.error('[crafExecutionService] Error getting CRAF approvers:', error);
      return [];
    }
  },

  // Get the most recent CRAF execution for a contract (if any)
  async getLatestCrafExecutionForContract(graphService, contractId) {
    try {
      const data = await graphService.getListItems(
        "CRAFExecutions",
        `fields/ContractID eq '${contractId}'`
      );
      const all = (data?.value || []).map(item => ({ ...item.fields, id: item.id }));
      if (all.length === 0) return null;
      all.sort((a, b) => new Date(b.InitiatedAt || 0) - new Date(a.InitiatedAt || 0));
      return all[0];
    } catch (error) {
      console.error('[crafExecutionService] Error getting latest CRAF execution:', error);
      return null;
    }
  },

  // Mark CRAF approver as signed/approved
  async approveCrafExecution(graphService, {
    approverId,
    executionId,
    approverEmail,
    signatureId,
    comments,
    signatureData,
    signaturePage,
    signatureX,
    signatureY,
    signatureWidth,
    signatureHeight,
    approverName,
    textBoxes = [],
    driveId,
    contractFolder
  }) {
    try {
      const execution = await this.getCrafExecution(graphService, executionId);

      // Embed the signature on the CRAF PDF BEFORE marking this approver as
      // Approved. If this fails for any reason (missing drive/folder, a
      // transient download/upload error, a bad signature image), it must
      // throw rather than be swallowed — otherwise the approver record gets
      // marked "Approved" while the PDF on disk was never actually signed,
      // which is exactly what was happening silently before this fix.
      if (!signatureData || !driveId || !contractFolder) {
        throw new Error('Missing signature data, drive, or contract folder — cannot embed signature on CRAF PDF');
      }

      console.log('[crafExecutionService] 🔄 Embedding signature on CRAF PDF...');
      const pdfFileName = getCrafPdfName(execution.ContractRefNo);

      let pdfBytes;
      try {
        pdfBytes = await graphService.downloadFile(driveId, `${contractFolder}/${pdfFileName}.pdf`);
      } catch (downloadError) {
        throw new Error(`Could not download CRAF PDF: ${downloadError.message}`);
      }
      if (!pdfBytes) {
        throw new Error('CRAF PDF download returned no data');
      }

      const signedPdfBytes = await pdfService.embedSignatureOnPdf(
        pdfBytes,
        signatureData,
        signaturePage || 1,
        signatureX || 50,
        signatureY || 130,
        signatureWidth || 300,
        signatureHeight || 120
      );

      let pdfWithText = signedPdfBytes;
      if (textBoxes && textBoxes.length > 0) {
        pdfWithText = await pdfService.addTextBoxesToPdf(
          signedPdfBytes,
          textBoxes,
          signaturePage || 1
        );
      }

      await graphService.uploadFile(
        driveId,
        contractFolder,
        `${pdfFileName}.pdf`,
        pdfWithText,
        "application/pdf"
      );

      console.log('[crafExecutionService] ✅ Saved signed CRAF PDF from', approverEmail);

      // Only now, with the PDF confirmed signed, record the approval.
      const approvalTime = new Date().toISOString();
      await graphService.updateListItem("CRAFApprovers", approverId, {
        Status: "Approved",
        SignatureID: signatureId,
        ApprovedAt: approvalTime,
        Comments: comments
      });

      const approvers = await this.getCrafExecutionApprovers(graphService, executionId);
      const completed = approvers.filter(a => a.Status === "Approved").length;

      await graphService.updateListItem("CRAFExecutions", executionId, {
        ApproversCompleted: completed
      });

      if (completed === approvers.length) {
        const completedAt = new Date().toISOString();
        await graphService.updateListItem("CRAFExecutions", executionId, {
          Status: "Completed",
          CompletedAt: completedAt
        });

        console.log('[crafExecutionService] ✅ All CRAF approvers signed - CRAF execution complete');

        try {
          await this.notifyCrafExecutionComplete(graphService, {
            contractRef: execution.ContractRefNo,
            contractTitle: execution.ContractTitle,
            approversCount: execution.TotalApprovers,
            completedAt: completedAt
          });
        } catch (notifyError) {
          console.error('[crafExecutionService] ⚠️ Failed to send CRAF completion notification:', notifyError);
        }
      } else {
        const nextApprover = approvers.find(a => a.Status === "Waiting");
        if (nextApprover) {
          await graphService.updateListItem("CRAFApprovers", nextApprover.id, {
            Status: "Pending"
          });

          try {
            await this.sendCrafApprovalReminder(graphService, {
              approverEmail: nextApprover.ApproverEmail,
              approverName: nextApprover.ApproverName,
              contractRef: execution.ContractRefNo,
              contractTitle: execution.ContractTitle,
              executionId: executionId
            });
          } catch (emailError) {
            console.error('[crafExecutionService] ⚠️ Failed to send CRAF approval email:', emailError);
          }
        }
      }
    } catch (error) {
      console.error('[crafExecutionService] Error approving CRAF execution:', error);
      throw error;
    }
  },

  async rejectCrafExecution(graphService, { approverId, reason }) {
    try {
      await graphService.updateListItem("CRAFApprovers", approverId, {
        Status: "Rejected",
        RejectionReason: reason,
        RejectedAt: new Date().toISOString()
      });
      console.log('[crafExecutionService] ✅ CRAF execution rejected');
    } catch (error) {
      console.error('[crafExecutionService] Error rejecting CRAF execution:', error);
      throw error;
    }
  },

  // Send CRAF completion notification to all admins (no UI notice — email only)
  async notifyCrafExecutionComplete(graphService, { contractRef, contractTitle, approversCount, completedAt }) {
    try {
      const allUsers = await graphService.getListItems("AllowedUsers");
      const adminEmails = (allUsers?.value || [])
        .filter(u => u.fields.Role === 'admin' || u.fields.Role === 'Admin')
        .map(u => u.fields.Title)
        .filter(email => email && email.trim());

      if (adminEmails.length > 0) {
        const emailBody = `
          <p>Hi,</p>
          <p>The CRAF (Contract Review and Approval Form) execution has been completed successfully:</p>
          <div style="background-color: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <p><strong>Contract:</strong> ${contractTitle}</p>
            <p><strong>Reference:</strong> ${contractRef}</p>
            <p><strong>CRAF Approvers:</strong> ${approversCount}</p>
            <p><strong>Completed:</strong> ${new Date(completedAt).toLocaleString()}</p>
          </div>
          <p>All required CRAF approvers have signed. The signed CRAF document is now available in the contract folder.</p>
          <p>Log in to LexCore to download the executed CRAF document.</p>
          <p>Best regards,<br/>LexCore Contract Management System</p>
        `;

        await graphService.sendEmail(
          adminEmails,
          `[LexCore] CRAF Execution Complete: ${contractRef}`,
          emailBody
        );

        console.log('[crafExecutionService] ✅ CRAF completion notification sent to', adminEmails.length, 'admin(s)');
      } else {
        console.warn('[crafExecutionService] ⚠️ No admins found to notify');
      }
    } catch (error) {
      console.error('[crafExecutionService] Error sending CRAF completion notification:', error);
    }
  },

  async sendCrafApprovalReminder(graphService, { approverEmail, approverName, contractRef, contractTitle, executionId }) {
    try {
      const executionLink = `${window.location.origin}/craf-approver/${executionId}/${executionId}`;

      const emailBody = `
        <p>Hi ${approverName},</p>
        <p>This is a reminder that you have a pending CRAF (Contract Review and Approval Form) approval:</p>
        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0;">
          <p><strong>Contract:</strong> ${contractTitle}</p>
          <p><strong>Reference:</strong> ${contractRef}</p>
        </div>
        <p>Please review and sign the CRAF document at your earliest convenience:</p>
        <p style="text-align: center; margin: 20px 0;">
          <a href="${executionLink}" style="display: inline-block; padding: 12px 24px; background-color: #ca8a04; color: white; text-decoration: none; border-radius: 6px; font-weight: bold;">
            Review & Sign CRAF
          </a>
        </p>
        <p>Best regards,<br/>LexCore Contract Management System</p>
      `;

      await graphService.sendEmail(
        [approverEmail],
        `[LexCore Reminder] CRAF Approval Pending: ${contractRef}`,
        emailBody
      );

      console.log('[crafExecutionService] ✅ CRAF reminder email sent to', approverEmail);
      return true;
    } catch (error) {
      console.error('[crafExecutionService] Error sending CRAF reminder email:', error);
      throw error;
    }
  }
};
